import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  List,
  ListSubheader,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useClientPlan } from '@/hooks/useClientPlan';
import { useAppSelector, selectBadges } from '@/store';
import { RouteConfig, routeConfig } from '@/routes';
import { sidebar } from '@/styles/tokens';
import { SidebarMenuItem } from './SidebarMenuItem';
import { ALL_MODES, MODES } from './sidebar.config';
import { SidebarMode } from './sidebar.types';
import { checkItemVisibility, getBadgeForRoute, isRouteSelected, resolveClientCardPath } from './sidebar.utils';
import { SidebarModeSwitcher } from './SidebarModeSwitcher';
import { UserAvatar } from '@components/common/UserAvatar.tsx';
import { CLIENT_OPTIONS_QUERY_KEY } from '@/hooks/useClientOptions';
import { getClientOptions } from '@/services/client.service';

export type { SidebarMode };

interface SidebarProps {
  isCollapsed: boolean;
  isMobile: boolean;
  onToggle: () => void;
  onNavigate?: (path: string) => void;
  onModeChange?: (mode: SidebarMode) => void;
  chatUnreadCount?: number;
}

export const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed,
  isMobile,
  onToggle,
  onNavigate,
  onModeChange,
  chatUnreadCount = 0,
}) => {
  const { t } = useTranslation();
  const { user, loading, isAuthenticated } = useAuth();
  const { hasFeature: hasClientFeature } = useClientPlan();
  const userRoles = useMemo(() => (user?.roles || []).map(r => r.toLowerCase()), [user?.roles]);
  const location = useLocation();
  const badges = useAppSelector(selectBadges);
  const queryClient = useQueryClient();
  
  const isClient = userRoles.includes('client');
  const showProfileInSidebar = !isClient && isAuthenticated;

  const availableModes = useMemo<SidebarMode[]>(() => {
    const modes: SidebarMode[] = [];
    if (userRoles.includes('client')) modes.push('client');
    if (userRoles.includes('expert')) modes.push('expert');
    if (modes.length === 0) modes.push('general');
    if (userRoles.some(r => ['superadmin', 'admin'].includes(r))) modes.push('admin');
    if (userRoles.includes('developer')) modes.push('developer');
    if (userRoles.includes('pm')) modes.push('pm');
    if (userRoles.includes('owner')) modes.push('owner');
    return modes;
  }, [userRoles]);

  const [currentMode, setCurrentMode] = useState<SidebarMode>(() => {
    const saved = localStorage.getItem('sidebar-mode') as SidebarMode;
    return saved && ALL_MODES.includes(saved) ? saved : 'general';
  });

  useEffect(() => {
    if (onModeChange) onModeChange(currentMode);
  }, [currentMode, onModeChange]);

  useEffect(() => {
    if (!loading && availableModes.length > 0 && !availableModes.includes(currentMode)) {
      // Синхронно записуємо у localStorage — інакше `resolvePushTarget`, `api.ts:X-Panel`
      // та інші читачі отримають застарілий 'general'/null замість реальної ролі.
      localStorage.setItem('sidebar-mode', availableModes[0]);
      setCurrentMode(availableModes[0]);
    }
  }, [availableModes, currentMode, loading]);

  // Client-role only: hide entirely (not a badge) unless the feature is granted — see workspace/current-work/standard-pro-feature-abstraction.md
  const isVisibleForMode = useCallback(
    (item: RouteConfig, mode: SidebarMode) =>
      checkItemVisibility(item, mode, userRoles, isAuthenticated) &&
      (mode !== 'client' || !item.requiredClientFeature || hasClientFeature(item.requiredClientFeature)),
    [userRoles, isAuthenticated, hasClientFeature],
  );

  // Експерт бачить лише своїх активних клієнтів, продакт - будь-якого.
  const canOpenClientCard = async (mode: SidebarMode, clientId: number): Promise<boolean> => {
    if (mode !== 'expert') return true;
    try {
      const options = await queryClient.fetchQuery({
        queryKey: CLIENT_OPTIONS_QUERY_KEY,
        queryFn: getClientOptions,
        staleTime: 5 * 60 * 1000,
      });
      return options.connectedClients.some(c => c.id === clientId && c.connectionStatus === 'active');
    } catch {
      return false;
    }
  };

  const handleModeChange = async (nextMode: SidebarMode) => {
    const prevMode = currentMode;
    localStorage.setItem('sidebar-mode', nextMode);
    setCurrentMode(nextMode);

    if (onNavigate) {
      // Відкрита картка клієнта зберігається при перемиканні експерт <-> продакт, якщо клієнт доступний.
      const clientCard = resolveClientCardPath(location.pathname, prevMode, nextMode);
      if (clientCard && (await canOpenClientCard(nextMode, clientCard.clientId))) {
        onNavigate(clientCard.path);
        return;
      }

      const visible = routeConfig.filter(
        r => r.showInSidebar && isVisibleForMode(r, nextMode),
      );
      const groups = visible.reduce((acc, r) => {
        const g = r.group || 'General';
        (acc[g] ??= []).push(r);
        return acc;
      }, {} as Record<string, RouteConfig[]>);

      const target =
        groups['General']?.[0]?.path ??
        groups[Object.keys(groups).find(g => g !== 'General') ?? '']?.[0]?.path ??
        (nextMode === 'admin' ? '/admin/news' : nextMode === 'developer' ? '/development' : '/dashboard');

      onNavigate(target);
    }
  };

  const collapsedGroupsKey = `sidebar-groups-collapsed:${currentMode}`;

  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem(collapsedGroupsKey) ?? '{}');
    } catch {
      return {};
    }
  });

  useEffect(() => {
    setCollapsedGroups(() => {
      try {
        return JSON.parse(localStorage.getItem(collapsedGroupsKey) ?? '{}');
      } catch {
        return {};
      }
    });
  }, [collapsedGroupsKey]);

  const toggleGroup = useCallback((group: string) => {
    setCollapsedGroups(prev => {
      const next = { ...prev, [group]: !prev[group] };
      localStorage.setItem(collapsedGroupsKey, JSON.stringify(next));
      return next;
    });
  }, [collapsedGroupsKey]);

  const groupedRoutes = useMemo(
    () =>
      routeConfig.reduce((acc, route) => {
        if (!route.showInSidebar) return acc;
        if (!isVisibleForMode(route, currentMode)) return acc;
        const g = route.group || 'General';
        (acc[g] ??= []).push(route);
        return acc;
      }, {} as Record<string, RouteConfig[]>),
    [currentMode, isVisibleForMode],
  );

  const activeConfig = MODES[currentMode];
  const isLight = currentMode === 'general' || currentMode === 'client' || currentMode === 'expert';
  const userName = user?.name || user?.email || '';

  return (
    <Box
      sx={{
        height: '100%',
        backgroundColor: activeConfig.bgColor,
        color: activeConfig.textColor,
        display: 'flex',
        flexDirection: 'column',
        transition: 'background-color 0.3s ease',
      }}
    >
      {/* Header */}
      <Toolbar sx={{ minHeight: '48px !important', px: '12px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: isCollapsed && !isMobile ? 'center' : 'flex-start' }}>
          {(!isCollapsed || isMobile) && (
            <Box sx={{ flexGrow: 1 }}>
              {loading ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={14} sx={{ color: activeConfig.textColor }} />
                  <Typography variant="body2" fontWeight={600}>{t('sidebar.loading')}</Typography>
                </Box>
              ) : (
                <Typography variant="subtitle1" noWrap fontWeight={700}>
                  {t(activeConfig.label)}
                </Typography>
              )}
            </Box>
          )}
          {!isMobile && (
            <IconButton
              size="small"
              onClick={onToggle}
              sx={{
                color: activeConfig.textColor,
                '&:hover': {
                  backgroundColor: isLight ? 'rgba(0, 0, 0, 0.04)' : sidebar.darkHoverMd,
                },
              }}
            >
              {isCollapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
            </IconButton>
          )}
        </Box>
      </Toolbar>

      <Divider sx={{ borderColor: isLight ? 'divider' : sidebar.darkDivider }} />

      {/* Nav items */}
      <List sx={{ flexGrow: 1, overflowY: 'auto', py: 0.5 }}>
        {groupedRoutes['General']?.map(item => (
          <SidebarMenuItem
            key={item.path}
            item={item}
            isCollapsed={isCollapsed}
            isMobile={isMobile}
            currentMode={currentMode}
            activeConfig={activeConfig}
            badgeContent={getBadgeForRoute(item, badges, chatUnreadCount)}
            isSelected={isRouteSelected(item, location.pathname)}
            onClick={() => onNavigate?.(item.path!)}
          />
        ))}

        {Object.keys(groupedRoutes)
          .filter(g => g !== 'General')
          .map(group => {
            const isGroupCollapsed = (!isCollapsed || isMobile) && !!collapsedGroups[group];
            return (
              <React.Fragment key={group}>
                <Divider sx={{ my: 0.5, borderColor: isLight ? 'divider' : sidebar.darkDivider }} />
                {(!isCollapsed || isMobile) && (
                  <ListSubheader
                    onClick={() => toggleGroup(group)}
                    sx={{
                      bgcolor: 'transparent',
                      lineHeight: '24px',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: isLight ? 'text.secondary' : sidebar.darkSubheaderText,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      userSelect: 'none',
                      py: 0.5,
                      pr: 1,
                      '&:hover': {
                        color: isLight ? 'text.primary' : sidebar.darkText,
                      },
                    }}
                  >
                    {t(group)}
                    {isGroupCollapsed
                      ? <ExpandMore fontSize="inherit" />
                      : <ExpandLess fontSize="inherit" />
                    }
                  </ListSubheader>
                )}
                <Collapse in={!isGroupCollapsed} timeout="auto" unmountOnExit>
                  {groupedRoutes[group].map(item => (
                    <SidebarMenuItem
                      key={item.path}
                      item={item}
                      isCollapsed={isCollapsed}
                      isMobile={isMobile}
                      currentMode={currentMode}
                      activeConfig={activeConfig}
                      badgeContent={getBadgeForRoute(item, badges, chatUnreadCount)}
                      isSelected={isRouteSelected(item, location.pathname)}
                      onClick={() => onNavigate?.(item.path!)}
                    />
                  ))}
                </Collapse>
              </React.Fragment>
            );
          })}
      </List>

      {/* User Profile Section (Only for non-clients) */}
      {showProfileInSidebar && (
        <>
          <Divider sx={{ borderColor: isLight ? 'divider' : sidebar.darkDivider }} />
          <Box
            onClick={() => onNavigate?.('/profile')}
            sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: isLight ? 'rgba(0, 0, 0, 0.04)' : sidebar.darkHoverMd,
              },
              transition: 'background-color 0.2s',
              justifyContent: isCollapsed && !isMobile ? 'center' : 'flex-start',
            }}
          >
            <UserAvatar path={user?.avatar} name={userName} sx={{ width: 32, height: 32 }} />
            {(!isCollapsed || isMobile) && (
              <Box sx={{ overflow: 'hidden' }}>
                <Typography variant="body2" fontWeight={600} noWrap fontSize="0.85rem">
                  {userName}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7 }} noWrap display="block" fontSize="0.75rem">
                  {t('sidebar.myProfile')}
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Mode switcher */}
      {availableModes.length > 1 && (
        <SidebarModeSwitcher
          currentMode={currentMode}
          availableModes={availableModes}
          isCollapsed={isCollapsed}
          isMobile={isMobile}
          onSelect={handleModeChange}
        />
      )}
    </Box>
  );
};
